"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "use-intl";

import { ExpandIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import {
  TRACKLIST_BODY_CLASS,
  TRACKLIST_SUMMARY_CLASS,
} from "@/components/music/tracklist-section";
import { TRACK_ROW_GRID_CLASS, TrackRowContent } from "@/components/track-row-content";
import { useVideoPlayer } from "@/contexts/video-player-context";
import { groupCatalogCreditsByRole } from "@/lib/catalog-credits-companies";
import { formatCreditRole } from "@/lib/format-credit-role";
import { formatDuration } from "@/lib/format-duration";
import { cn } from "@/lib/utils";
import type { RouterOutputs } from "@/types/rpc";
import { categorizeFormat } from "@garimpa/lib/formatClassification";
import type { Medium } from "@garimpa/lib/musicTypes";

export type { Medium };

export type TrackVideo = RouterOutputs["music"]["getTrackVideos"][number];

/** Above this many tracks, multi-disc releases open with only the first disc expanded. */
const COLLAPSE_MEDIA_TRACK_THRESHOLD = 20;
/** Disc filter chips appear from this many discs up… */
const MEDIA_FILTER_MIN_COUNT = 3;
/** …and turn into a compact select above this many (box sets with 100+ discs). */
const MEDIA_CHIP_MAX_COUNT = 12;
/** Disc headers shown before "show more" on very large box sets. */
const MEDIA_HEADER_PAGE_SIZE = 20;

function tracksDurationSeconds(tracks: Medium["tracks"]): number | null {
  if (tracks.length === 0 || tracks.some((track) => track.durationSeconds == null)) return null;
  return tracks.reduce((sum, track) => sum + (track.durationSeconds ?? 0), 0);
}

function formatTracksDuration(tracks: Medium["tracks"]): string | null {
  const seconds = tracksDurationSeconds(tracks);
  return seconds ? formatDuration(seconds) : null;
}

/** A single disc split into exactly two sides reads best as two columns on wide screens. */
function hasTwoSideColumns(media: Medium[]): boolean {
  const [medium] = media;
  return (
    media.length === 1 &&
    medium.sections.length === 2 &&
    medium.sections.every((section) => section.kind === "side")
  );
}

type TrackCredits = Medium["tracks"][number]["credits"];

function TrackCreditsDetails({ credits }: { credits: TrackCredits }) {
  const tr = useTranslations("shared.creditRoles");
  const t = useTranslations("release");
  const creditGroups = groupCatalogCreditsByRole(credits);
  if (creditGroups.length === 0) return null;

  return (
    // Indented to line up with the track title (padding + number + play slot + gaps).
    <details className="group/credits me-3 mb-2.5 ms-24 sm:ms-[5.5rem] md:ms-24">
      <summary className="flex min-h-8 w-fit cursor-pointer max-sm:min-h-11 list-none items-center gap-1 rounded text-xs font-bold text-primary/85 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
        {t("credits")}
        <ExpandIcon
          className="text-sm transition-transform group-open/credits:rotate-180"
          decorative
        />
      </summary>
      <dl className="mt-1.5 grid grid-cols-1 gap-x-5 gap-y-2.5 rounded-lg bg-card px-3.5 py-3 sm:grid-cols-2">
        {creditGroups.map((group) => (
          <div key={group.key} className="min-w-0">
            <dt className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {tr.has(group.role) ? tr(group.role) : formatCreditRole(group.role)}
            </dt>
            <dd>
              <ul className="flex min-w-0 flex-wrap gap-x-2 gap-y-1 text-[0.8125rem] font-semibold">
                {group.credits.map((credit) => (
                  <li
                    key={`${credit.role}-${credit.artistId}`}
                    className="after:ml-2 after:text-muted-foreground after:content-['·'] last:after:content-none"
                  >
                    <Link
                      appearance="text"
                      to={`/artist/${credit.artistId}`}
                      className="break-words text-foreground"
                    >
                      {credit.artistName}
                    </Link>
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

interface TrackRowsProps {
  currentYoutubeId: string | null;
  onPlay: (youtubeId: string) => void;
  tracks: Medium["tracks"];
  trackVideosByTrackId: Map<string, TrackVideo>;
}

function TrackRows({ currentYoutubeId, onPlay, tracks, trackVideosByTrackId }: TrackRowsProps) {
  return (
    <ol className="list-none border-t border-border">
      {tracks.map((track) => {
        const youtubeId = trackVideosByTrackId.get(track.id)?.youtubeId ?? null;
        const isPlaying = youtubeId !== null && currentYoutubeId === youtubeId;

        return (
          <li
            key={track.id}
            id={`track-${track.id}`}
            data-track-position={track.number}
            className={cn(
              "border-b border-border/60 transition-colors hover:bg-secondary/40",
              isPlaying && "bg-primary/10 hover:bg-primary/10",
            )}
          >
            <div className={cn(TRACK_ROW_GRID_CLASS, "min-h-12 px-1 py-1.5 md:px-3")}>
              <TrackRowContent
                isPlaying={isPlaying}
                onPlay={onPlay}
                track={track}
                youtubeId={youtubeId}
              />
            </div>
            <TrackCreditsDetails credits={track.credits} />
          </li>
        );
      })}
    </ol>
  );
}

function SectionHeading({
  as: Heading,
  duration,
  id,
  title,
}: {
  as: "h3" | "h4";
  duration: string | null;
  id: string;
  title: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-1 pb-2.5 md:px-3">
      <Heading
        id={id}
        className="min-w-0 text-xs font-bold uppercase tracking-wider text-muted-foreground"
      >
        {title}
      </Heading>
      {duration && (
        <span className="shrink-0 text-xs font-bold tabular-nums tracking-wider text-muted-foreground">
          {duration}
        </span>
      )}
    </div>
  );
}

function useSectionTitle() {
  const t = useTranslations("release");
  return (section: Medium["sections"][number]): string | null => {
    if (section.kind !== "side") return section.title ?? section.label;
    return section.title
      ? t("sideTitle", { side: section.label, title: section.title })
      : t("side", { side: section.label });
  };
}

interface MediumSectionsProps extends TrackRowsProps {
  medium: Medium;
  showHeader: boolean;
  twoColumns: boolean;
}

function MediumSections({
  medium,
  showHeader,
  twoColumns,
  ...trackRowsProps
}: MediumSectionsProps) {
  const sectionTitleOf = useSectionTitle();
  const SectionTitle = showHeader ? "h4" : "h3";

  if (medium.sections.length === 0) {
    return <TrackRows {...trackRowsProps} tracks={medium.tracks} />;
  }

  return (
    <div className={cn("grid grid-cols-1 gap-y-6", twoColumns && "lg:grid-cols-2 lg:gap-x-8")}>
      {medium.sections.map((section, sectionIndex) => {
        const sectionTitle = sectionTitleOf(section);
        const titleId = `medium-${medium.position}-section-${sectionIndex}-title`;
        const SectionContainer = sectionTitle ? "section" : "div";

        return (
          <SectionContainer
            key={`${section.kind}-${section.label}-${sectionIndex}`}
            aria-labelledby={sectionTitle ? titleId : undefined}
            className="min-w-0"
          >
            {sectionTitle && (
              <SectionHeading
                as={SectionTitle}
                duration={formatTracksDuration(section.tracks)}
                id={titleId}
                title={sectionTitle}
              />
            )}
            {section.sourceTitles.slice(1).map((title, titleIndex) => (
              <p
                key={`${title}-${titleIndex}`}
                className="mb-2 px-1 text-xs font-bold text-muted-foreground md:px-3"
              >
                {title}
              </p>
            ))}
            <TrackRows {...trackRowsProps} tracks={section.tracks} />
          </SectionContainer>
        );
      })}
    </div>
  );
}

interface TracklistMediumProps extends TrackRowsProps {
  expanded: boolean;
  hidden?: boolean;
  medium: Medium;
  onToggle: () => void;
  showHeader: boolean;
  twoColumns: boolean;
}

function useMediumSideRange() {
  const t = useTranslations("release");
  return (medium: Medium): string | null => {
    const sides = medium.sections.filter((section) => section.kind === "side");
    if (sides.length === 0) return null;
    if (sides.length === 1) return t("side", { side: sides[0].label });
    return t("sideRange", { first: sides[0].label, last: sides[sides.length - 1].label });
  };
}

function TracklistMediumHeader({
  expanded,
  medium,
  onToggle,
}: {
  expanded: boolean;
  medium: Medium;
  onToggle: () => void;
}) {
  const tf = useTranslations("shared.formats");
  const t = useTranslations("release");
  const ta = useTranslations("album");
  const sideRangeOf = useMediumSideRange();
  const formatLabel = medium.format && tf.has(medium.format) ? tf(medium.format) : medium.format;
  const formatCategory = categorizeFormat(medium.format) ?? "other";
  const sideRange = sideRangeOf(medium);
  const summary = [
    ta("tracks", { count: medium.tracks.length }),
    formatTracksDuration(medium.tracks),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <h3 id={`medium-${medium.position}-title`}>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={`medium-${medium.position}-tracks`}
        onClick={onToggle}
        className="flex min-h-14 w-full items-center gap-3 bg-card px-4 text-left transition-colors hover:bg-secondary focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary md:px-5"
      >
        <span className="min-w-0 flex-1 text-base font-bold">
          {t("medium", {
            category: formatCategory,
            number: medium.position,
            format: formatLabel ?? "",
            hasFormat: formatLabel ? "yes" : "no",
          })}
          {sideRange && (
            <span className="font-medium text-muted-foreground">{` · ${sideRange}`}</span>
          )}
        </span>
        <span className="shrink-0 text-[0.8125rem] tabular-nums text-muted-foreground">
          {summary}
        </span>
        <ExpandIcon
          className={cn(
            "shrink-0 text-lg text-muted-foreground transition-transform",
            expanded && "rotate-180",
          )}
          decorative
        />
      </button>
    </h3>
  );
}

function TracklistMedium({
  expanded,
  hidden = false,
  medium,
  onToggle,
  showHeader,
  twoColumns,
  ...trackRowsProps
}: TracklistMediumProps) {
  const MediumContainer = showHeader ? "section" : "div";

  return (
    <MediumContainer
      hidden={hidden}
      aria-labelledby={showHeader ? `medium-${medium.position}-title` : undefined}
      className={cn(showHeader && "overflow-hidden rounded-xl border border-border")}
    >
      {showHeader && (
        <TracklistMediumHeader expanded={expanded} medium={medium} onToggle={onToggle} />
      )}
      {/* Collapsed discs stay in the DOM (hidden) so every track remains indexable. */}
      <div
        id={`medium-${medium.position}-tracks`}
        hidden={!expanded}
        className={cn(showHeader && "px-2 pb-3 pt-4 md:px-3")}
      >
        <MediumSections
          {...trackRowsProps}
          medium={medium}
          showHeader={showHeader}
          tracks={medium.tracks}
          twoColumns={twoColumns}
        />
        {medium.orphanSectionTitles.map((title, titleIndex) => (
          <p
            key={`${title}-${titleIndex}`}
            data-source-section
            className="px-1 pt-4 text-xs font-bold uppercase tracking-wider text-muted-foreground md:px-3"
          >
            {title}
          </p>
        ))}
      </div>
    </MediumContainer>
  );
}

function initialExpandedMedia(media: Medium[]): Set<number> {
  const trackCount = media.reduce((sum, medium) => sum + medium.tracks.length, 0);
  if (trackCount <= COLLAPSE_MEDIA_TRACK_THRESHOLD) {
    return new Set(media.map((medium) => medium.position));
  }
  return new Set(media.slice(0, 1).map((medium) => medium.position));
}

function MediaFilterChips({
  media,
  selected,
  onSelect,
}: {
  media: Medium[];
  selected: number | null;
  onSelect: (position: number | null) => void;
}) {
  const t = useTranslations("release");
  const ta = useTranslations("album");
  const trackCount = media.reduce((sum, medium) => sum + medium.tracks.length, 0);
  const chipClass = (active: boolean) =>
    cn(
      "inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm font-semibold sm:h-9 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
      active
        ? "border-transparent bg-foreground text-background"
        : "border-border text-foreground hover:bg-secondary",
    );

  if (media.length > MEDIA_CHIP_MAX_COUNT) {
    return (
      <label className="flex items-center gap-2 pb-2 text-sm font-semibold">
        <span className="text-muted-foreground">{t("jumpToMedium")}</span>
        <select
          value={selected ?? ""}
          onChange={(event) =>
            onSelect(event.currentTarget.value ? Number(event.currentTarget.value) : null)
          }
          className="h-11 rounded-md border border-border bg-secondary/50 px-3 text-sm text-foreground sm:h-10 focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">
            {t("allMedia")} · {ta("tracks", { count: trackCount })}
          </option>
          {media.map((medium) => (
            <option key={medium.position} value={medium.position}>
              {t("disc", { number: medium.position })}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <div className="flex flex-wrap gap-2 pb-2">
      <button
        type="button"
        aria-pressed={selected === null}
        onClick={() => onSelect(null)}
        className={chipClass(selected === null)}
      >
        {t("allMedia")} · {ta("tracks", { count: trackCount })}
      </button>
      {media.map((medium) => (
        <button
          key={medium.position}
          type="button"
          aria-pressed={selected === medium.position}
          onClick={() => onSelect(medium.position)}
          className={chipClass(selected === medium.position)}
        >
          {t("disc", { number: medium.position })}
        </button>
      ))}
    </div>
  );
}

function findPlayingMediumPosition(
  media: Medium[],
  currentYoutubeId: string | null,
  trackVideosByTrackId: Map<string, TrackVideo>,
): number | null {
  if (!currentYoutubeId) return null;
  const medium = media.find((candidate) =>
    candidate.tracks.some(
      (track) => trackVideosByTrackId.get(track.id)?.youtubeId === currentYoutubeId,
    ),
  );
  return medium?.position ?? null;
}

/**
 * Track count · total duration, rendered as the first grid item so `TracklistSection`
 * places it on the heading row (right-aligned). Both release and work pages get it
 * without passing anything extra.
 */
function TracklistSummary({ media }: { media: Medium[] }) {
  const ta = useTranslations("album");
  const tracks = media.flatMap((medium) => medium.tracks);
  if (tracks.length === 0) return null;
  const summary = [ta("tracks", { count: tracks.length }), formatTracksDuration(tracks)]
    .filter(Boolean)
    .join(" · ");

  return (
    <p className={cn("text-sm tabular-nums text-muted-foreground", TRACKLIST_SUMMARY_CLASS)}>
      {summary}
    </p>
  );
}

export function Tracklist({
  media,
  onPlay,
  trackVideos = [],
}: {
  media: Medium[];
  onPlay: (youtubeId: string) => void;
  trackVideos?: TrackVideo[];
}) {
  const tr = useTranslations("release");
  const { currentTrack } = useVideoPlayer();
  const currentYoutubeId = currentTrack?.youtubeId ?? null;
  const trackVideosByTrackId = useMemo(
    () => new Map(trackVideos.map((trackVideo) => [trackVideo.trackId, trackVideo])),
    [trackVideos],
  );
  const [expandedMedia, setExpandedMedia] = useState(() => initialExpandedMedia(media));
  const [selectedMedium, setSelectedMedium] = useState<number | null>(null);
  const [visibleMediaCount, setVisibleMediaCount] = useState(MEDIA_HEADER_PAGE_SIZE);
  const isMultiMedium = media.length > 1;
  const twoColumns = hasTwoSideColumns(media);
  // Every disc stays rendered (hidden when filtered out or beyond the page) so all
  // tracks remain in the SSR output; only visibility changes.
  const isMediumVisible = (medium: Medium, index: number) =>
    selectedMedium === null ? index < visibleMediaCount : medium.position === selectedMedium;
  const hiddenMediaCount =
    selectedMedium === null ? Math.max(0, media.length - visibleMediaCount) : 0;

  // Open the disc holding the track that starts playing, so it is never hidden.
  const playingMediumPosition = findPlayingMediumPosition(
    media,
    currentYoutubeId,
    trackVideosByTrackId,
  );
  useEffect(() => {
    if (playingMediumPosition === null) return;
    setExpandedMedia((current) =>
      current.has(playingMediumPosition) ? current : new Set([...current, playingMediumPosition]),
    );
  }, [playingMediumPosition]);

  const toggleMedium = (position: number) =>
    setExpandedMedia((current) => {
      const next = new Set(current);
      if (next.has(position)) next.delete(position);
      else next.add(position);
      return next;
    });
  const selectMedium = (position: number | null) => {
    setSelectedMedium(position);
    if (position !== null) setExpandedMedia((current) => new Set([...current, position]));
  };

  return (
    <>
      <TracklistSummary media={media} />
      <div className={cn("space-y-3", TRACKLIST_BODY_CLASS)}>
        {media.length >= MEDIA_FILTER_MIN_COUNT && (
          <MediaFilterChips media={media} selected={selectedMedium} onSelect={selectMedium} />
        )}
        {media.map((medium, index) => (
          <TracklistMedium
            key={medium.position}
            hidden={!isMediumVisible(medium, index)}
            currentYoutubeId={currentYoutubeId}
            expanded={!isMultiMedium || expandedMedia.has(medium.position)}
            medium={medium}
            onPlay={onPlay}
            onToggle={() => toggleMedium(medium.position)}
            showHeader={isMultiMedium}
            tracks={medium.tracks}
            trackVideosByTrackId={trackVideosByTrackId}
            twoColumns={twoColumns}
          />
        ))}
        {hiddenMediaCount > 0 && (
          <button
            type="button"
            onClick={() => setVisibleMediaCount((count) => count + MEDIA_HEADER_PAGE_SIZE)}
            className="flex min-h-11 w-full items-center justify-center rounded-lg border border-dashed border-border text-sm font-semibold text-primary transition-colors hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {tr("showMoreMedia", { count: Math.min(hiddenMediaCount, MEDIA_HEADER_PAGE_SIZE) })}
          </button>
        )}
      </div>
    </>
  );
}
